import type { UserRole } from '@/features/courses/useCourse';
import type { NormalizedQuestionData } from '@/features/questions/questionNormalizer';

interface AIHomeworkVisibilityParams {
  hasLoadedHint?: boolean;
  // Whether the section is one the student can actually see — open OR past its
  // due date. AI help is NOT gated on the due date: a closed assignment is
  // exactly when a student is reviewing and most wants a hint or extra practice.
  isSectionAvailable: boolean;
  question: NormalizedQuestionData;
  role: UserRole;
  sectionKind: 'homework' | 'preLecture';
}


function isAIHintsAllowed(question: NormalizedQuestionData) {
  return question.allowAIHints === true;
}

// Deliberately does NOT check question.isReviewMode. Review mode means "past due
// or instructor stats view", and both are states where an enabled AI hint or
// practice problem should still be offered. Closing an assignment ends
// submissions, not AI help.
function isAvailableHomeworkQuestion({
  isSectionAvailable,
  question,
  role,
  sectionKind,
}: AIHomeworkVisibilityParams) {
  return (
    (role === 'student' || role === 'instructor') &&
    sectionKind === 'homework' &&
    question.isAssigned &&
    isSectionAvailable
  );
}

// AI hints have their OWN attempt threshold (aiHintThreshold), independent of
// the authored-hint setting. 0 (or unset) means available immediately — it
// deliberately does not inherit the authored-hint meaning of 0, which used to
// hide AI hints for every course that never changed that default.
function hasMetHintThreshold(question: NormalizedQuestionData) {
  const threshold = question.aiHintThreshold ?? 0;

  if (threshold <= 0) {
    return true;
  }

  return question.attemptsUsed >= threshold;
}

export function getAIHintVisibility(params: AIHomeworkVisibilityParams) {
  const { hasLoadedHint = false, question } = params;

  // NOTE: an authored hint no longer suppresses the AI hint. Enabling AI hints
  // is now entirely independent of whether the question has its own hint and of
  // whether authored hints are visible.
  return (
    isAIHintsAllowed(question) &&
    isAvailableHomeworkQuestion(params) &&
    !hasLoadedHint &&
    hasMetHintThreshold(question)
  );
}

// Practice problems are their own setting. They used to be enabled by the AI
// hints flag; allowAIPracticeProblems falls back to it only when nothing has
// been authored, so pre-split chapters are unaffected.
export function getPracticeVisibility(params: AIHomeworkVisibilityParams) {
  return (
    params.question.allowAIPracticeProblems === true &&
    isAvailableHomeworkQuestion(params)
  );
}

export function useAIHintVisibility(params: AIHomeworkVisibilityParams) {
  return {
    showHintButton: getAIHintVisibility(params),
    showPracticeButton: getPracticeVisibility(params),
  };
}
